// src/components/learners/LearnerRegistrationForm.jsx
// Learner registration/edit form with immediate data population from navigation state
// Uses row-based layout that stacks on mobile devices

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  School, 
  Users, 
  Save, 
  Upload, 
  Download,
  Trash2,
  Plus,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Camera,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { LearnerService } from '../../services/learnerService';
import ProfileImage from './ProfileImage';

const LearnerRegistrationForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  // Use navigation state if available for immediate population
  const initialLearner = location.state?.learner || null;
  // Format initial learner data for form if available
  const getInitialFormData = () => {
    if (initialLearner) {
      return {
        surname: initialLearner.surname || initialLearner.learnerSurname || '',
        firstName: initialLearner.firstName || initialLearner.learnerName || '',
        secondName: initialLearner.secondName || '',
        gender: initialLearner.gender || '',
        dateOfBirth: formatDateForInput(initialLearner.dateOfBirth),
        identityNumber: initialLearner.identityNumber || '',
        mobileNumber: initialLearner.mobileNumber || '',
        email: initialLearner.email || '',
        physicalAddress: initialLearner.physicalAddress || '',
        parentGuardianType: initialLearner.parentGuardianType || 'parent',
        parentGuardianName: initialLearner.parentGuardianName || '',
        parentMobile: initialLearner.parentMobile || '',
        parentGuardianEmail: initialLearner.parentGuardianEmail || '',
        relationship: initialLearner.relationship || '',
        schoolName: initialLearner.schoolName || '',
        schoolTel: initialLearner.schoolTel || '',
        schoolEmail: initialLearner.schoolEmail || '',
        enrolledSubjects: initialLearner.enrolledSubjects?.length > 0 
          ? initialLearner.enrolledSubjects 
          : ['']
      };
    }
    return {
      surname: '',
      firstName: '',
      secondName: '',
      gender: '',
      dateOfBirth: '',
      identityNumber: '',
      mobileNumber: '',
      email: '',
      physicalAddress: '',
      parentGuardianType: 'parent',
      parentGuardianName: '',
      parentMobile: '',
      parentGuardianEmail: '',
      relationship: '',
      schoolName: '',
      schoolTel: '',
      schoolEmail: '',
      enrolledSubjects: ['']
    };
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    if (date.toDate) {
      return date.toDate().toISOString().split('T')[0];
    }
    // If it's already a string in YYYY-MM-DD format, return as is
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}/)) {
      return date.split('T')[0];
    }
    return '';
  };

  const [formData, setFormData] = useState(getInitialFormData);

  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(initialLearner?.profileImageUrl || null);
  const [loading, setLoading] = useState(false);
  // Only show loading if editing AND we don't have initial data from navigation
  const [loadingData, setLoadingData] = useState(isEdit && !initialLearner);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  
  const fileInputRef = useRef(null);

  // Load learner data if editing and no initial data from navigation state
  useEffect(() => {
    if (isEdit && id) {
      loadLearnerData();
    }
  }, [isEdit, id]);

  const loadLearnerData = async () => {
    try {
      if (!initialLearner) {
        setLoadingData(true);
      }
      setError(null);
      
      const learner = await LearnerService.getLearner(id);
      
      if (!learner) {
        throw new Error('Learner not found');
      }

      // Format the data properly for the form
      let formattedDate = '';
      if (learner.dateOfBirth) {
        let date;
        if (learner.dateOfBirth instanceof Date) {
          date = learner.dateOfBirth;
        } else if (learner.dateOfBirth?.toDate) {
          date = learner.dateOfBirth.toDate();
        } else {
          date = new Date(learner.dateOfBirth);
        }
        formattedDate = date.toISOString().split('T')[0];
      }

      const formattedData = {
        surname: learner.surname || '',
        firstName: learner.firstName || '',
        secondName: learner.secondName || '',
        gender: learner.gender || '',
        dateOfBirth: formattedDate,
        identityNumber: learner.identityNumber || '',
        mobileNumber: learner.mobileNumber || '',
        email: learner.email || '',
        physicalAddress: learner.physicalAddress || '',
        parentGuardianType: learner.parentGuardianType || 'parent',
        parentGuardianName: learner.parentGuardianName || '',
        parentMobile: learner.parentMobile || '',
        parentGuardianEmail: learner.parentGuardianEmail || '',
        relationship: learner.relationship || '',
        schoolName: learner.schoolName || '',
        schoolTel: learner.schoolTel || '',
        schoolEmail: learner.schoolEmail || '',
        grade: learner.grade || '',
        enrolledSubjects: learner.enrolledSubjects?.length > 0
          ? learner.enrolledSubjects
          : ['']
      };

      setFormData(formattedData);
      
      if (learner.profileImageUrl) {
        setProfileImagePreview(learner.profileImageUrl);
      }
      
    } catch (err) {
      console.error('Error loading learner data:', err);
      setError(err.message || 'Failed to load learner data');
    } finally {
      setLoadingData(false);
    }
  };

  // Profile image handlers
  const handleProfileImageSelect = (file) => {
    setProfileImage(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfileImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    setError(null);
  };

  const removeProfileImage = () => {
    setProfileImage(null);
    setProfileImagePreview(null);
  };

  // Form field handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleSubjectChange = (index, value) => {
    const newSubjects = [...formData.enrolledSubjects];
    newSubjects[index] = value;
    setFormData(prev => ({
      ...prev,
      enrolledSubjects: newSubjects
    }));
  };

  const addSubject = () => {
    setFormData(prev => ({
      ...prev,
      enrolledSubjects: [...prev.enrolledSubjects, '']
    }));
  };

  const removeSubject = (index) => {
    if (formData.enrolledSubjects.length > 1) {
      const newSubjects = formData.enrolledSubjects.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        enrolledSubjects: newSubjects
      }));
    }
  };

  // Relationship options based on parent/guardian type
  const getRelationshipOptions = () => {
    if (formData.parentGuardianType === 'parent') {
      return ['Mother', 'Father'];
    } else {
      return ['Grandmother', 'Grandfather', 'Aunt', 'Uncle', 'Sister', 'Brother', 'Other'];
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.surname.trim()) {
      setError('Surname is required');
      return false;
    }
    if (!formData.firstName.trim()) {
      setError('First Name is required');
      return false;
    }
    if (!formData.gender) {
      setError('Gender is required');
      return false;
    }
    if (!formData.dateOfBirth) {
      setError('Date of Birth is required');
      return false;
    }
    if (!formData.identityNumber.trim()) {
      setError('Identity Number is required');
      return false;
    }
    if (!formData.parentGuardianName.trim()) {
      setError('Parent/Guardian Name is required');
      return false;
    }
    if (!formData.relationship) {
      setError('Relationship is required');
      return false;
    }
    if (!formData.schoolName.trim()) {
      setError('School Name is required');
      return false;
    }
    
    const validSubjects = formData.enrolledSubjects.filter(subject => subject.trim() !== '');
    if (validSubjects.length === 0) {
      setError('At least one enrolled subject is required');
      return false;
    }

    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cleanData = {
        ...formData,
        surname: formData.surname.trim(),
        firstName: formData.firstName.trim(),
        secondName: formData.secondName.trim(),
        dateOfBirth: new Date(formData.dateOfBirth),
        parentGuardianName: formData.parentGuardianName.trim(),
        schoolName: formData.schoolName.trim(),
        enrolledSubjects: formData.enrolledSubjects.filter(subject => subject.trim() !== '')
      };

      if (isEdit && id) {
        await LearnerService.updateLearner(id, cleanData, profileImage);
        setSuccess('Learner updated successfully');
        setTimeout(() => navigate('/learners'), 1500);
      } else {
        await LearnerService.createLearner(cleanData, profileImage);
        setSuccess('Learner registered successfully');
        setTimeout(() => {
          setFormData(getInitialFormData());
          setProfileImage(null);
          setProfileImagePreview(null);
          setSuccess(null);
          navigate('/learners');
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving learner:', err);
      setError(err.message || 'Failed to save learner');
    } finally {
      setLoading(false);
    }
  };

  // CSV Import
  const handleCsvImport = async () => {
    if (!csvFile) return;

    setCsvImporting(true);
    setCsvResults(null);

    try {
      const results = await LearnerService.importLearnersFromCSV(csvFile);
      setCsvResults(results);
      if (results.successful > 0) {
        setShowCsvUpload(false);
      }
    } catch (err) {
      setCsvResults({ error: err.message || 'Failed to import CSV' });
    } finally {
      setCsvImporting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const headers = ['Surname*', 'First Name*', 'Second Name', 'Gender*', 'Date of Birth* (YYYY-MM-DD)', 'Identity Number*', 'Mobile Number', 'Email', 'Physical Address', 'Parent/Guardian Type', 'Parent/Guardian Name*', 'Parent Mobile', 'Parent Email', 'Relationship*', 'School Name*', 'School Tel', 'School Email', 'Grade', 'Enrolled Subjects (comma-separated)'];
    const csv = headers.join(',');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'learner_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading learner data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/learners')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Learners</span>
              </button>
              <div className="h-6 border-l border-slate-300 hidden sm:block"></div>
              <h1 className="text-xl font-semibold text-slate-900 hidden sm:block">
                {isEdit ? 'Edit Learner' : 'Register New Learner'}
              </h1>
            </div>
            
            {!isEdit && (
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadCsvTemplate}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors text-sm"
                >
                  <Download className="h-4 w-4" />
                  Template
                </button>
                <button
                  onClick={() => setShowCsvUpload(!showCsvUpload)}
                  className="flex items-center gap-2 px-3 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-sm"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Import CSV</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Mobile Title */}
        <h1 className="text-xl font-semibold text-slate-900 mb-6 sm:hidden">
          {isEdit ? 'Edit Learner' : 'Register New Learner'}
        </h1>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-3 text-rose-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3 text-emerald-700">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* CSV Upload Section */}
        {showCsvUpload && (
          <div className="mb-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Import Learners from CSV</h3>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <FileText className="h-12 w-12 text-slate-400 mb-3" />
                  <span className="text-slate-700 font-medium">
                    {csvFile ? csvFile.name : 'Click to select CSV file'}
                  </span>
                  <span className="text-sm text-slate-500 mt-1">or drag and drop</span>
                </label>
              </div>
              
              {csvResults && (
                <div className={`p-4 rounded-xl ${csvResults.error ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                  {csvResults.error ? (
                    <p>{csvResults.error}</p>
                  ) : (
                    <p>Successfully imported {csvResults.successful} of {csvResults.total} learners</p>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCsvUpload(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCsvImport}
                  disabled={!csvFile || csvImporting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {csvImporting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Import
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image Section */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Camera className="h-5 w-5 text-slate-500" />
              Profile Photo
            </h3>
            
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <ProfileImage
                src={profileImagePreview}
                alt="Profile preview"
                size="2xl"
              />
              
              <div className="flex-1 text-center sm:text-left">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleProfileImageSelect(e.target.files[0])}
                  className="hidden"
                />
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Photo
                  </button>
                  {profileImagePreview && (
                    <button
                      type="button"
                      onClick={removeProfileImage}
                      className="px-4 py-2 border border-rose-200 text-rose-600 rounded-lg hover:bg-rose-50 flex items-center justify-center gap-2 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-2">JPG, PNG or WebP. Max 5MB.</p>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Personal Information
            </h3>
            
            {/* Row-based layout that stacks on mobile */}
            <div className="space-y-4">
              {/* Row 1: Names */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Surname <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => handleInputChange('surname', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Surname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    First Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="First Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Second Name
                  </label>
                  <input
                    type="text"
                    value={formData.secondName}
                    onChange={(e) => handleInputChange('secondName', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Second Name"
                  />
                </div>
              </div>

              {/* Row 2: Gender, DOB, ID */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Gender <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Date of Birth <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Identity Number <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.identityNumber}
                    onChange={(e) => handleInputChange('identityNumber', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="ID Number"
                  />
                </div>
              </div>

              {/* Row 3: Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="tel"
                      value={formData.mobileNumber}
                      onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Mobile Number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Address */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Physical Address
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <textarea
                    value={formData.physicalAddress}
                    onChange={(e) => handleInputChange('physicalAddress', e.target.value)}
                    rows={2}
                    className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                    placeholder="Enter physical address"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Parent/Guardian Information */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-500" />
              Parent/Guardian Information
            </h3>
            
            <div className="space-y-4">
              {/* Row 1: Type, Name, Relationship */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Type <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formData.parentGuardianType}
                    onChange={(e) => {
                      handleInputChange('parentGuardianType', e.target.value);
                      handleInputChange('relationship', '');
                    }}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="parent">Parent</option>
                    <option value="guardian">Guardian</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.parentGuardianName}
                    onChange={(e) => handleInputChange('parentGuardianName', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Full Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Relationship <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={formData.relationship}
                    onChange={(e) => handleInputChange('relationship', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">Select Relationship</option>
                    {getRelationshipOptions().map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="tel"
                      value={formData.parentMobile}
                      onChange={(e) => handleInputChange('parentMobile', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Mobile Number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      value={formData.parentGuardianEmail}
                      onChange={(e) => handleInputChange('parentGuardianEmail', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* School Information */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <School className="h-5 w-5 text-amber-500" />
              School Information
            </h3>
            
            <div className="space-y-4">
              {/* Row 1: School Name, Grade */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="sm:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    School Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolName}
                    onChange={(e) => handleInputChange('schoolName', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="School Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Grade
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => handleInputChange('grade', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  >
                    <option value="">Select Grade</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={String(i + 1)}>Grade {i + 1}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: School Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    School Telephone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="tel"
                      value={formData.schoolTel}
                      onChange={(e) => handleInputChange('schoolTel', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="School Tel"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    School Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      value={formData.schoolEmail}
                      onChange={(e) => handleInputChange('schoolEmail', e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="school@example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Enrolled Subjects */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Enrolled Subjects <span className="text-rose-500">*</span>
                </label>
                <div className="space-y-2">
                  {formData.enrolledSubjects.map((subject, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={subject}
                        onChange={(e) => handleSubjectChange(index, e.target.value)}
                        className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder={`Subject ${index + 1}`}
                      />
                      {formData.enrolledSubjects.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSubject(index)}
                          className="p-2.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addSubject}
                  className="mt-2 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add Subject
                </button>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={() => navigate('/learners')}
              className="px-6 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEdit ? 'Update Learner' : 'Register Learner'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LearnerRegistrationForm;